import { type LucideIcon, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WizardStepperProps {
  steps: { label: string; icon: LucideIcon }[];
  currentStep: number;
  completedSteps: number[];
  onStepClick: (step: number) => void;
}

export default function WizardStepper({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
}: WizardStepperProps) {
  const isCompleted = (index: number) => completedSteps.includes(index);
  const isCurrent = (index: number) => index === currentStep;
  const isFuture = (index: number) => !isCurrent(index) && !isCompleted(index);

  return (
    <>
      {/* Desktop stepper */}
      <nav className="hidden md:block" aria-label="Wizard progress">
        <ol className="flex items-center justify-between">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const completed = isCompleted(index);
            const current = isCurrent(index);
            const future = isFuture(index);

            return (
              <li key={index} className="flex-1 flex items-center">
                {/* Connector line before (skip first) */}
                {index > 0 && (
                  <div
                    className={cn(
                      'h-0.5 flex-1 transition-colors',
                      completed || current
                        ? 'bg-[var(--color-primary-500)]'
                        : 'bg-stone-200'
                    )}
                  />
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (completed) onStepClick(index);
                  }}
                  disabled={future}
                  className={cn(
                    'flex flex-col items-center gap-1.5 group relative',
                    completed && 'cursor-pointer',
                    future && 'cursor-default'
                  )}
                  aria-current={current ? 'step' : undefined}
                >
                  {/* Circle */}
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center transition-all border-2',
                      current &&
                        'bg-[var(--color-primary-600)] border-[var(--color-primary-600)] text-white shadow-md',
                      completed &&
                        'bg-green-500 border-green-500 text-white hover:bg-green-600 hover:border-green-600',
                      future && 'bg-white border-stone-300 text-stone-400'
                    )}
                  >
                    {completed ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>

                  {/* Label */}
                  <span
                    className={cn(
                      'text-xs font-medium whitespace-nowrap',
                      current && 'text-[var(--color-primary-700)]',
                      completed && 'text-green-700',
                      future && 'text-stone-400'
                    )}
                  >
                    {step.label}
                  </span>
                </button>

                {/* Connector line after (skip last) */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'h-0.5 flex-1 transition-colors',
                      isCompleted(index) ? 'bg-[var(--color-primary-500)]' : 'bg-stone-200'
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Mobile stepper: current step with arrows */}
      <nav className="md:hidden" aria-label="Wizard progress">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (currentStep > 0) onStepClick(currentStep - 1);
            }}
            disabled={currentStep === 0}
            className={cn(
              'p-2 rounded-lg transition-colors',
              currentStep > 0
                ? 'text-stone-600 hover:bg-stone-100'
                : 'text-stone-300 cursor-default'
            )}
            aria-label="Previous step"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 flex-1 justify-center">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[var(--color-primary-600)] text-white">
              {(() => {
                const Icon = steps[currentStep].icon;
                return <Icon className="w-4 h-4" />;
              })()}
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-text-primary">
                {steps[currentStep].label}
              </p>
              <p className="text-xs text-text-muted">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (currentStep < steps.length - 1 && isCompleted(currentStep))
                onStepClick(currentStep + 1);
            }}
            disabled={
              currentStep === steps.length - 1 || !isCompleted(currentStep)
            }
            className={cn(
              'p-2 rounded-lg transition-colors',
              currentStep < steps.length - 1 && isCompleted(currentStep)
                ? 'text-stone-600 hover:bg-stone-100'
                : 'text-stone-300 cursor-default'
            )}
            aria-label="Next step"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mt-3">
          {steps.map((_, index) => (
            <div
              key={index}
              className={cn(
                'h-1.5 rounded-full transition-all',
                isCurrent(index) && 'w-6 bg-[var(--color-primary-500)]',
                isCompleted(index) && !isCurrent(index) && 'w-1.5 bg-green-400',
                isFuture(index) && 'w-1.5 bg-stone-200'
              )}
            />
          ))}
        </div>
      </nav>
    </>
  );
}
